import { ManageAccounts, Person } from '@mui/icons-material';
import { Box, Typography, Tooltip, Checkbox, Button, FormControlLabel, Alert, Divider } from '@mui/material';
import React, { useState } from 'react';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { deleteFamilyMember } from 'src/shared/services/familyService';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { DateHelper } from 'src/shared/helpers/dateHelper';
import { FamilyMemberNew } from '../models/familyMember';
import { FormMemberFamilyMemberPut, MemberFamilyMemberPutRequest } from '../models/formMemberFamilyPut';
import { MemberFamilyDeleteSecedePickers } from './memberFamilyDeleteSecedePickers';

type MemberFamilyDeleteFamilyMemberModalProps = {
  modalOpen: boolean;
  onClose: () => void;
  member: FamilyMemberNew;
  refetchOnDelete: () => void;
};

export const MemberFamilyDeleteFamilyMemberModal = (props: MemberFamilyDeleteFamilyMemberModalProps) => {
  const { modalOpen, onClose, member, refetchOnDelete } = props;
  const { t } = useTranslation();
  const isMounted = useIsMounted();

  const [loading, setLoading] = useState(false);
  const [checked, setChecked] = useState(false);

  const MemberFamilyMemberPutSchema = yup.object().shape({
    idToSecede: yup.number().required(),
    resCause: yup.string().required(`${t('member:family.required')}`),
    resDate: yup.string().required(`${t('member:family.required')}`),
  });

  const formMethods = useForm<FormMemberFamilyMemberPut>({
    resolver: yupResolver(MemberFamilyMemberPutSchema),
    defaultValues: {
      idToSecede: 0,
      resCause: '',
      resDate: '',
    },
  });

  const { handleSubmit, setValue, reset } = formMethods;

  const onCloseHandler = () => {
    onClose();
  };

  const checkboxHandler = (event: React.ChangeEvent<HTMLInputElement>, familymemberId: number) => {
    if (event.target.checked) {
      setValue('idToSecede', familymemberId);
      setChecked(true);
    } else {
      setValue('idToSecede', 0);
      setChecked(false);
      reset();
    }
  };

  // Körs om medlem bara ska tas bort i hushåll
  const deleteFamilyMemberHandler = async (req: MemberFamilyMemberPutRequest) => {
    ConsoleHelper.log('deleteFamilyMember::', req);

    setLoading(true);
    try {
      await deleteFamilyMember(member.family_id, req);
      if (isMounted()) {
        toast.success(<>{t('member:family.connectionDeletedSuccess')}</>);
        setChecked(false);
        reset();
        refetchOnDelete();
      }
    } catch (error) {
      if (isMounted()) {
        ConsoleHelper.log('onSubmitDeleteFamilyMember', error);
        toast.error(<>{t('member:family.deleteError')}</>);
      }
    } finally {
      if (isMounted()) setLoading(false);
    }
  };

  // Körs om medlem ska utträdas
  const onSubmitWithSecede = (data: FormMemberFamilyMemberPut) => {
    ConsoleHelper.log('onSubmitDeleteFamilyMember::', data);
    const dataToPut: MemberFamilyMemberPutRequest = {
      ...data,
      resDate: DateHelper.getFSDate(data.resDate),
    };
    deleteFamilyMemberHandler(dataToPut);
  };

  return (
    <GenericDialog
      open={modalOpen}
      onClose={onCloseHandler}
      onSave={() => ConsoleHelper.log('save')}
      maxWidth='xs'
      showDialogActions={false}
      padding={0}
      dialogTitle={t('member:family.deleteFamilyMember')}
      dialogContent={
        <>
          <Alert severity='warning'>
            <Typography mb={1}>{`${t('member:family.invoiceInfoForHouseholdMember')}`}</Typography>
            <Typography mb={1}>{`${t('member:family.deleteOneMemberSameTime')}`}</Typography>
          </Alert>
          <Divider />
          <FormProvider {...formMethods}>
            <form onSubmit={handleSubmit(onSubmitWithSecede)}>
              <Box display='flex' flexDirection='column' p={2}>
                <Box display='flex' flexDirection='row' justifyContent='flex-end'>
                  <Typography fontWeight={500}>{t('member:family.secede')}</Typography>
                </Box>
                <Box key={member.memberNr} display='flex' flexDirection='row' alignItems='center' justifyContent='space-between'>
                  <Box display='flex' flexDirection='row' alignItems='center'>
                    {member.mainMember ? (
                      <Tooltip title={t('member:memberDetails.headMember')} arrow>
                        <ManageAccounts fontSize='small' />
                      </Tooltip>
                    ) : (
                      <Tooltip title={t('member:memberDetails.familyMember')} arrow>
                        <Person fontSize='small' />
                      </Tooltip>
                    )}
                    <Typography ml={1}>{`${member.firstName} ${member.lastName}`}</Typography>
                  </Box>
                  <Box>
                    <FormControlLabel
                      label=''
                      labelPlacement='start'
                      control={<Checkbox name='idToSecede' checked={checked} onChange={(event) => checkboxHandler(event, member.familymember_id)} />}
                    />
                  </Box>
                </Box>
                {checked && <MemberFamilyDeleteSecedePickers checked={checked} />}
              </Box>
              <Box
                display='flex'
                flexDirection='row'
                justifyContent='flex-end'
                width='100%'
                p={1}
                sx={{ borderTop: `1px solid rgba(0, 0, 0, 0.12)` }}
              >
                <Button disabled={loading} variant='outlined' color='primary' disableElevation onClick={onClose} sx={{ mr: 1 }}>
                  {t('common:button.cancel')}
                </Button>
                {checked ? (
                  <Button disabled={loading} type='submit' autoFocus variant='contained' color='primary' disableElevation>
                    {t('common:button.yes')}
                  </Button>
                ) : (
                  <Button
                    disabled={loading}
                    onClick={() => deleteFamilyMemberHandler({})}
                    autoFocus
                    variant='contained'
                    color='primary'
                    disableElevation
                  >
                    {t('common:button.yes')}
                  </Button>
                )}
              </Box>
            </form>
          </FormProvider>
        </>
      }
    />
  );
};
